import React from 'react';
import type { CurrencyCode } from '@bbpos/types';

/**
 * Shared presentation tokens for the tenant screens.
 *
 * Lifted out of TenantDetailPage so the Website tab renders identically to the
 * rest of the page without importing from a page module (which would invert the
 * usual component → page dependency direction).
 */

export const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

export function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

// A tenant has no `country` column: the country IS its immutable
// `default_currency`, which drives the tax regime (INR → GST India, AED → VAT
// UAE, …). Keyed by CurrencyCode so the list can never drift from
// SUPPORTED_CURRENCIES — the single source of truth in @bbpos/types.
export const COUNTRY_BY_CURRENCY: Readonly<Record<CurrencyCode, string>> = {
  INR: 'India',
  AED: 'United Arab Emirates',
  SAR: 'Saudi Arabia',
  QAR: 'Qatar',
  OMR: 'Oman',
  BHD: 'Bahrain',
  KWD: 'Kuwait',
};

/** "Oman (OMR)" — legacy tenants without a currency are INR. */
export function formatTenantCountry(currency: string | null | undefined): string {
  const code = (currency || 'INR') as CurrencyCode;
  const country = COUNTRY_BY_CURRENCY[code];
  return country ? `${country} (${code})` : code;
}

export type KpiTone = 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'teal' | 'violet';

export function toneClasses(tone: KpiTone) {
  switch (tone) {
    case 'success':
      return { icon: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', accent: 'from-emerald-500/20 to-emerald-500/0' };
    case 'teal':
      return { icon: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400', accent: 'from-teal-500/18 to-teal-500/0' };
    case 'violet':
      return { icon: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400', accent: 'from-violet-500/18 to-violet-500/0' };
    case 'warning':
      return { icon: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400', accent: 'from-amber-500/18 to-amber-500/0' };
    case 'danger':
      return { icon: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400', accent: 'from-rose-500/18 to-rose-500/0' };
    case 'info':
      return { icon: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400', accent: 'from-sky-500/18 to-sky-500/0' };
    default:
      return { icon: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400', accent: 'from-indigo-500/18 to-indigo-500/0' };
  }
}

export function KpiStatCard({
  label,
  value,
  supporting,
  tone,
  icon,
  onClick,
}: {
  label: string;
  value: string | number;
  supporting?: string;
  tone: KpiTone;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  const palette = toneClasses(tone);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        GLASS_PANEL,
        'relative overflow-hidden p-5 text-left transition-all duration-200 hover:shadow-lg w-full',
        onClick && 'cursor-pointer',
      )}
    >
      <div className={cn('absolute inset-x-0 top-0 h-14 bg-gradient-to-b', palette.accent)} />
      <div className="relative space-y-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50 tabular-nums">
            {value}
          </p>
        </div>
        {supporting && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{supporting}</p>
        )}
      </div>
    </button>
  );
}
